import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useMutation } from '@tanstack/react-query';
import isEmpty from 'lodash-es/isEmpty';
import React, { useMemo, useState } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import { remove, removeAll } from '../../../api/ventura/location';
import { headCells } from '../../../constants/locationTable';
import useAdGroupLocations from '../../../hooks/useAdGroupLocations';
import { useConfig } from '../../../hooks/useConfig';
import useDebounce from '../../../hooks/useDebounce';
import useModal from '../../../hooks/useModal';
import Link from '../../../inmarkit/Link';
import { AdGroup } from '../../../validationSchemas/AdGroupSchema';
import BatchLocationForm from './BatchLocationForm';
import CreateLocationFileForm from './CreateLocationFileForm';
import EsriLocationBuilder from './EsriLocationBuilder/EsriLocationBuilder';
import LocationForm from './LocationForm';
import LocationTableItem from './LocationTableItem';

function LocationTable() {
  const { getValues, watch }: UseFormReturn<AdGroup> = useFormContext();
  const conquest = watch('conquest');

  const [adGroupId, adGroupName, lineItemId, momentsType] = getValues([
    'id',
    'name',
    'insertionOrderId',
    'momentsType',
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data, isLoading, isSuccess, page, setPage, perPage, setPerPage, refetch } = useAdGroupLocations(
    adGroupId,
    false,
    debouncedSearchTerm
  );
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * perPage - (data?.pagination?.total_items || 0)) : 0;
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const { apiHost } = useConfig();

  const handlePageChange = (_event: unknown, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(data.collection?.map((location) => location.id));
      return;
    }
    setSelected([]);
  };

  const deleteLocation = useMutation({
    mutationFn: (locationId: number) => remove(apiHost, locationId),
    onSuccess: (response) => {
      // For success, server returns:
      // {"status":"Removed location asdfasdf (312189843)"}
      // For errors, server returns:
      // {"code":404,"exception":"RecordNotFound"}
      // So we'll assume if there are exceptions, then it was successful.
      if (!response.exception) {
        refetch();
      }
    },
  });

  const deleteAllLocations = useMutation({
    mutationFn: () => removeAll(apiHost, adGroupId),
    onSuccess: (response) => {
      // For success, server returns:
      // {"status":"Removed location asdfasdf (312189843)"}
      // For errors, server returns:
      // {"code":404,"exception":"RecordNotFound"}
      // So we'll assume if there are exceptions, then it was successful.
      if (!response.exception) {
        refetch();
      }
    },
  });

  const deleteSelected = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete the selected locations?')) {
      selected.forEach((locationId) => {
        deleteLocation.mutate(locationId);
      });
    }
  };

  const deleteAllSelected = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete all locations?')) {
      deleteAllLocations.mutate();
    }
  };

  const batchLocationFormProps = useMemo(
    () => ({
      adGroupId,
      adGroupName,
      lineItemId,
      momentsType,
    }),
    [adGroupId, adGroupName, lineItemId, momentsType]
  );

  // eslint-disable-next-line react/jsx-props-no-spreading
  const [showAddLocationForm, closeAddLocationForm] = useModal(
    () => (
      <LocationForm location={null} adGroupId={adGroupId} adGroupName={adGroupName} onClose={closeAddLocationForm} />
    ),
    [adGroupId, adGroupName]
  );
  const [showBatchAddLocationForm, closeBatchAddLocationForm] = useModal(
    () => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <BatchLocationForm {...batchLocationFormProps} onClose={closeBatchAddLocationForm} />
    ),
    [batchLocationFormProps]
  );
  const [showBatchUpdateLocationForm, closeBatchUpdateLocationForm] = useModal(
    () => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <BatchLocationForm {...batchLocationFormProps} onClose={closeBatchUpdateLocationForm} isUpdate />
    ),
    [batchLocationFormProps]
  );
  const [showCreateLocationFileForm, closeCreateLocationFileForm] = useModal(
    () => (
      <CreateLocationFileForm adGroupId={adGroupId} adGroupName={adGroupName} onClose={closeCreateLocationFileForm} />
    ),
    [adGroupId, adGroupName]
  );

  const [showEsriLocationBuilder, closeEsriLocationBuilder] = useModal(
    () => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <EsriLocationBuilder {...batchLocationFormProps} onClose={closeEsriLocationBuilder} />
    ),
    [batchLocationFormProps]
  );

  const [editLocation, setEditLocation] = useState(null);

  const [showEditLocationForm, closeEditLocationForm] = useModal(
    () => (
      <LocationForm
        location={editLocation}
        adGroupId={adGroupId}
        adGroupName={adGroupName}
        onClose={closeEditLocationForm}
      />
    ),
    [editLocation, adGroupId, adGroupName]
  );

  const locationsCount = data?.pagination?.total_items || 0;

  return (
    <>
      <Typography marginBottom={2}>
        {conquest ? 'Conquest ' : ''}Locations ({locationsCount})
      </Typography>
      <Paper variant="outlined">
        <Stack padding={2} spacing={2}>
          <Stack direction="row" spacing={1} justifyContent="end">
            {!isEmpty(data?.collection) && (
              <>
                {!isEmpty(selected) && (
                  <Button onClick={() => deleteSelected()} variant="outlined" color="error">
                    Delete
                  </Button>
                )}
                <Button onClick={() => deleteAllSelected()} variant="outlined" color="error">
                  Delete All
                </Button>
                <Button onClick={showBatchUpdateLocationForm} variant="outlined">
                  Batch Update
                </Button>
              </>
            )}
            <Button onClick={showEsriLocationBuilder} variant="outlined">
              ESRI Audience
            </Button>
            <Button onClick={showCreateLocationFileForm} variant="outlined">
              Create Location File
            </Button>
            <Button onClick={showBatchAddLocationForm} variant="outlined">
              Batch Add
            </Button>
            <Button onClick={showAddLocationForm}>Add</Button>
          </Stack>
            <Stack direction="row">
              <Link
                target="_blank"
                href={`${apiHost}/admin/campaigns/${adGroupId}/locations/index_csv`}
                pt={1}
              >
                Download (CSV)
              </Link>
            </Stack>
          <Stack>
            <TextField
              sx={{ backgroundColor: 'white', width: '100%' }}
              variant="outlined"
              placeholder="Search by Location Name Or POI ID"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              value={searchTerm}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    {isLoading ? <CircularProgress color="inherit" size={24} /> : null}
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isSuccess && selected.length > 0 && selected.length < data.collection?.length}
                      checked={isSuccess && data.collection?.length > 0 && selected.length === data.collection?.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : 'left'}
                      padding={headCell.id === 'name' ? 'none' : 'normal'}
                    >
                      <Typography>{headCell.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 5 }, () => Math.random()).map((rowItem) => (
                    <TableRow key={rowItem} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {Array.from({ length: 15 }, () => Math.random()).map((colItem) => (
                        <TableCell key={colItem} scope="row">
                          <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {isSuccess &&
                  data.collection?.map((location) => (
                    <LocationTableItem
                      key={location.id}
                      location={location}
                      checked={selected.indexOf(location.id) !== -1}
                      onSelectChange={(checked) => {
                        if (checked) {
                          setSelected([...selected, location.id]);
                        } else {
                          setSelected(selected.filter((id) => id !== location.id));
                        }
                      }}
                      onClick={() => {
                        setEditLocation(location);
                        showEditLocationForm();
                      }}
                    />
                  ))}
                {isSuccess && emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={headCells.length + 1} />
                  </TableRow>
                )}
                {isSuccess && (
                  <TableRow key="locations-pagination">
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 500]}
                      count={data.pagination?.total_items || 0}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={perPage}
                      onRowsPerPageChange={handlePerPageChange}
                    />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </>
  );
}

export default LocationTable;
